import { useMutation } from '@apollo/client'
import { Box, FormModal, Link, Text } from '@chaine/keychaine'
import React, { useState } from 'react'
import { SubmitHandler } from 'react-hook-form'

import { Toast } from '../../../_shared/components/toast'
import { validations } from '../../../_utils/validations/validations'
import { AppModalProps } from '../components/apps-data'
import { ENABLE_VERIZON_TELOGIS_APP, EnableVerizonTelogisAppInput } from '../data/mutations'

/**
 * Saves VerizonTelogis app's username and password.
 *
 * @param props.app the app
 * @param props.isOpen sets the modal to open or closed
 * @param props.onClose closes the modal
 */
const VerizonTelogisModal = React.memo((props: AppModalProps) => {
  const { isOpen, onClose, appType, refetchApps } = props
  const { showToast } = Toast()
  const [formError, setFormError] = useState('')

  const [enableVerizonTelogisApp, { loading }] = useMutation<
    { enableFleetLocateApp: { apiKey: string; password: string; username: string } },
    EnableVerizonTelogisAppInput
  >(ENABLE_VERIZON_TELOGIS_APP, {
    onCompleted: () => {
      showToast({
        status: 'success',
        title: `Verizon Telogis app is enabled successfully!`
      })
      refetchApps()
      onClose()
      setFormError('')
    },
    onError: (err) => {
      if (err.message === 'Your username or password is invalid.') {
        setFormError('Invalid username/password. Please try again.')
        showToast({
          status: 'error',
          title: `Invalid username/password. Please try again.`
        })
      } else {
        setFormError(`Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`)
        showToast({
          status: 'error',
          title: `Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`
        })
      }
    }
  })

  const closeModal = () => {
    setFormError('')
    onClose()
  }

  type FieldValues = { appPassword: string; appUsername: string }
  const handleSaveDetails: SubmitHandler<FieldValues> = (event) => {
    enableVerizonTelogisApp({
      variables: {
        appType: appType,
        password: event.appPassword,
        username: event.appUsername
      }
    })
  }

  const footer = (
    <Box>
      <Text color="red.600">{formError}</Text>
      <Text>
        {`See `}
        <Link
          color="blue.600"
          href="https://handbook.chaineapp.com/product-guide/carriers/connect-eld/elds/telogis"
          target="_blank"
          rel="noopener noreferrer"
        >
          detailed steps here.
        </Link>
      </Text>
    </Box>
  )

  return (
    <>
      <FormModal
        isOpen={isOpen}
        onClose={closeModal}
        title={`Enable Verizon-Telogis for Chaine`}
        subtitle={`Enabling this integration requires an admin Verizon-Telogis account. Enter your username and password.`}
        formSubmitHandler={handleSaveDetails}
        buttonText={'Enable'}
        initialValues={{}}
        validations={validations}
        isLoading={loading}
        footer={formError ? footer : <></>}
        modalItems={[
          {
            labelText: 'Verizon-Telogis username',
            name: 'appUsername',

            placeHolder: 'Enter your Verizon-Telogis username',
            type: 'input'
          },
          {
            labelText: 'Verizon-Telogis password',
            name: 'appPassword',

            placeHolder: 'Enter your Verizon-Telogis password',
            type: 'input'
          }
        ]}
      />
    </>
  )
})

VerizonTelogisModal.displayName = 'VerizonTelogisModal'

export { VerizonTelogisModal }
